import { bulbMedium, femaleQuestionMedium } from '@/assets/icons';

const trs = {
  W5H1_TITLE: `5W1H`,
  W5H1_WHAT_EXAMPLES: `What is it?\nWhat is the current state of the situation?\nWhat are the consequences?\nWhat are the risks?`,
  W5H1_WHO_EXAMPLES: `Who is affected?\nWho is describing the problem?\nWho is affected by the consequences of the problem?\nWho will be afffected by the resolution?`,
  W5H1_WHERE_EXAMPLES: `Where does it happen?\nWhat place(s) does the problem involve?\nIn which workshop, which device?\nWhat are the environmental conditions?`,
  W5H1_WHEN_EXAMPLES: `How long have you had this problem?\nWhen does it occur?\nWhen has it been detected?\nDoes it happen repeatedly? If so, how?\nWhen does it get dangerous?`,
  W5H1_HOW_EXAMPLES: `How does it happen?\nWhat are the conditions / circumstances for it to happen?\nHow should we proceed?\nAre there any methods?\nHow to setup the resolution, what procedures?`,
  W5H1_HOWMUCH_EXAMPLES: `How much water is flooding the workshop?\nHow many defects until the problem's detection?`,
  W5H1_WHY_EXAMPLES: `Why is it a problem?`,
};

const W5H1 = `
<p>
  The 5W1H or 5W2H method helps to collect basic but sufficient information on all dimensions of the problem.
  With the help of this tool, everyone can participate in the complete identification of all essential aspects of the subject.
  <br>
  It features a constructive critical analysis approach based on systematic questioning.
  <br><b>5W2H</b> : <b>W</b>hat? <b>W</b>ho? <b>W</b>here? <b>W</b>hen? <b>H</b>ow? <b>H</b>ow much? <b>W</b>hy?
</p>
<br>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">These questions are too simple, you can't describe a problem precisely in this way.</span>
</div>
<br>
<p>
  The more one uses this tool, the more one notices that each of these simple questions hides several others.
  Let me go into more detail:
</p>
<h2 class="basic-sub-title">
  @:message.WHAT?
</h2>
<p>
  This question is asked for a better description of the problem.
  All physical manifestations of the problem can be described here.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHAT_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHO?
</h2>
<p>
  This question helps everyone to understand who is affected by the problem.
  Identifying the different stakeholders makes problem solving more effective.
  And we should even go a step further:
  Knowing who to consult to check the reliability of the causes or the effectiveness of the actions, for example, is essential.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHO_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHERE?
</h2>
<p>
  This question concerns the description of locations. The problem may occur
  in one place for one reason and in another place for another. It is not just a question of
  identifying the building, but of identifying all the features of the location that
  may impact on the problem, such as the material of the floor, or the type of lighting.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHERE_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHEN?
</h2>
<p>
  This question is asked in order to define the time frame. This will help you
  to relate problems that occur in the same temporal circumstances, or with the
  same frequency.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_WHEN_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.HOW?
</h2>
<p>
  The answers to this question will lead to a better understanding of how
  to find and solve the problem.
  <br>
  Ensure that this is exhaustive. If 10 steps need to be reproduced for the problem to occur,
  all 10 steps should be described in detail. Identifying the procedure for reproducing
  a problem is essential to begin to determine the causes.
  <br>
  This step is also a very effective recurrence elimination tool, and
  it is often sufficient to replicate the replication procedure of one problem to detect others.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOW_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.HOW_MUCH?
</h2>
<p>
  This question helps to quantify everything about the problem. This question completes the
  "@:message.HOW" by highlighting quantifiable notions about the circumstances in which the problem
  arises.
  <br/>
  <div class="ia-sensei-example">
    <div class="example-title">Examples:</div>
    <div class="ia-sensei-example__content--preline">@:sensei.W5H1_HOWMUCH_EXAMPLES</div>
  </div>
</p>
<h2 class="basic-sub-title">
  @:message.WHY?
</h2>
<p>
  This can be a tricky question. We should not ask "Why is the problem happening?",
  but rather "Why is this situation a problem?". Let us briefly recall our course on
  "@:sensei.SPOT_PB_TITLE" : @:sensei.WHAT_IS_A_PB
  So what is the ideal situation?
  <br/>
</p>
<br/>
<p>
  Remember to always complete the 5W1H tool before starting the rest of the resolution.
</p>
`;


export default {
  ...trs,
  W5H1,
};
